import clsx from "clsx";
import React, { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useHistory } from "react-router-dom";

import {
  AppBar,
  Collapse,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Drawer,
} from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { ExpandLess, ExpandMore, Person, Language as GlobeIcon } from "@material-ui/icons";
import CloseIcon from "@material-ui/icons/Close";
import MenuIcon from "@material-ui/icons/Menu";
import PeopleIcon from "@material-ui/icons/People";
import MoneyOffIcon from "@material-ui/icons/MoneyOff";

import { FleetContext } from "../../context";
import { Brand } from "../Icons/Brand";
import Scooter from "../Icons/Scooter";
import Permissions from "../../permissions";
import PermissionedDisplay from "../Layout/PermissionedDisplay";

const useStyles = makeStyles((theme) => ({
  horizontalNav: {
    backgroundColor: theme.palette.background.paper,
  },
  toolbarHeight: {
    height: theme.spacing(theme.navSpacingSize),
  },
  fleetsList: {
    backgroundColor: theme.palette.background.default,
  },
  selected: {
    backgroundColor: theme.palette.background.sidebarSelected,
  },
  brand: {
    height: theme.spacing(theme.navSpacingSize),
    width: theme.spacing(theme.navSpacingSize),
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: theme.palette.primary.red,
  },
  daBurger: {
    marginLeft: "auto",
    width: theme.spacing(theme.navSpacingSize),
    height: theme.spacing(theme.navSpacingSize),
  },
  padding: {
    padding: theme.spacing(2, 4, 2, 4),
  },
  indent: {
    marginLeft: theme.spacing(4),
  },
}));

export default function HorizontalNav() {
  const theme = useTheme();
  const classes = useStyles(theme);
  const { fleets, activeFleet } = useContext(FleetContext);
  const [menuOpen, setMenuOpen] = useState(false);
  const [fleetsOpen, setFleetsOpen] = useState(false);
  const { t } = useTranslation();
  const history = useHistory();
  // eslint-disable-next-line no-unused-vars

  const toggleFleets = () => {
    setFleetsOpen((prev) => !prev);
  };

  const toggleMenu = () => {
    setMenuOpen((prev) => !prev);
    setFleetsOpen(false);
  };

  return (
    <>
      <AppBar
        position="fixed"
        className={classes.horizontalNav}
        aria-label={t("HeaderNav.MobileMenu")}
        style={{ zIndex: 2000 }}
      >
        <Toolbar disableGutters className={classes.toolbarHeight}>
          <Link to="/" className={classes.brand} aria-label={t("HeaderNav.Home")}>
            <IconButton>
              <Brand client="superpedestrian" />
            </IconButton>
          </Link>
          <IconButton
            color="inherit"
            aria-label={t("HeaderNav.OpenMenu")}
            className={classes.daBurger}
            onClick={toggleMenu}
          >
            {menuOpen ? <CloseIcon fontSize="large" /> : <MenuIcon fontSize="large" />}
          </IconButton>
        </Toolbar>
      </AppBar>
      <Drawer open={menuOpen} anchor="top" onClose={toggleMenu}>
        <List component="nav" aria-label={t("HeaderNav.MenuOptions")} disablePadding>
          <ListItem className={classes.toolbarHeight}></ListItem>
          <Divider />
          <ListItem
            button
            onClick={() => {
              if (fleets.length) toggleFleets();
              else history.push("/fleets");
            }}
            className={classes.padding}
          >
            <ListItemIcon>
              <GlobeIcon />
            </ListItemIcon>
            <ListItemText primary={t("HeaderNav.Fleets")} />
            {fleets.length &&
              (fleetsOpen ? <ExpandLess fontSize="large" /> : <ExpandMore fontSize="large" />)}
          </ListItem>
          <Collapse
            in={fleetsOpen}
            timeout="auto"
            unmountOnExit
            onClick={toggleMenu}
            className={classes.fleetsList}
          >
            <Divider />
            {fleets.map((fleet) => (
              <List
                component="div"
                aria-label={fleet.name}
                disablePadding
                key={fleet.uuid4}
                className={activeFleet?.uuid4 === fleet?.uuid4 ? classes.selected : null}
              >
                <ListItem
                  button
                  onClick={() => history.push(`/fleets/${fleet.uuid4}/`)}
                  className={clsx(classes.padding, classes.indent)}
                >
                  <ListItemText primary={fleet.name} inset />
                </ListItem>
              </List>
            ))}
            <PermissionedDisplay permission={Permissions.writeFleets}>
              <List component="div" disablePadding>
                <ListItem
                  button
                  onClick={() => history.push("/fleets/new")}
                  className={classes.padding}
                >
                  <ListItemText
                    primary={t("HeaderNav.NewFleet")}
                    className={clsx(classes.padding, classes.indent)}
                    style={{ color: theme.palette.text.red }}
                  />
                </ListItem>
              </List>
            </PermissionedDisplay>
          </Collapse>
          <Divider />
          <ListItem button onClick={() => history.push("/vehicles")} className={classes.padding}>
            <ListItemIcon>
              <Scooter />
            </ListItemIcon>
            <ListItemText primary={t("HeaderNav.Vehicles")} />
          </ListItem>
          <Divider />
          <ListItem button onClick={() => history.push("/riders")} className={classes.padding}>
            <ListItemIcon>
              <PeopleIcon />
            </ListItemIcon>
            <ListItemText primary={t("HeaderNav.Riders")} />
          </ListItem>
          <Divider />
          <PermissionedDisplay permission={Permissions.readPromos}>
            <ListItem button onClick={() => history.push("/promos")} className={classes.padding}>
              <ListItemIcon>
                <MoneyOffIcon />
              </ListItemIcon>
              <ListItemText primary={t("HeaderNav.Promos")} />
            </ListItem>
          </PermissionedDisplay>
          <Divider />
          <ListItem button onClick={() => history.push("/logout")} className={classes.padding}>
            <ListItemIcon>
              <Person />
            </ListItemIcon>
            <ListItemText primary={t("HeaderNav.Logout")} />
          </ListItem>
        </List>
      </Drawer>
    </>
  );
}
